/** @format — Client-side shipment list filtering + sorting */

import { getShipmentStatusLabel } from "@/utils/shipmentStatus";

export type ShipmentDateSort = "newest" | "oldest" | "eta_soonest" | "eta_latest";

export type ShipmentListFilters = {
  search: string;
  status: string;
  dateSort: ShipmentDateSort;
};

const parseDate = (value?: string | null) => {
  if (!value) return 0;
  const ts = new Date(value).getTime();
  return Number.isFinite(ts) ? ts : 0;
};

export function getShipmentStatusOptions(shipments: any[]) {
  const statuses = new Set<string>();
  shipments.forEach((s) => {
    if (s?.status) statuses.add(String(s.status).toLowerCase());
  });
  return ["all", ...Array.from(statuses).sort()];
}

export function filterShipments(shipments: any[], filters: ShipmentListFilters) {
  const q = filters.search.trim().toLowerCase();

  let result = shipments.filter((s) => {
    if (q) {
      const shipmentNumber = String(s.number ?? "").toLowerCase();
      const matchesSearch =
        s.vehicle?.toLowerCase().includes(q) ||
        shipmentNumber.includes(q) ||
        `#${shipmentNumber}`.includes(q) ||
        s.id?.toLowerCase().includes(q) ||
        (s._raw?.vehicle?.vin || "").toLowerCase().includes(q);
      if (!matchesSearch) return false;
    }

    if (filters.status !== "all") {
      if (String(s.status || "").toLowerCase() !== filters.status) return false;
    }

    return true;
  });

  result = [...result].sort((a, b) => {
    const bookingA = parseDate(a._raw?.booking_date);
    const bookingB = parseDate(b._raw?.booking_date);
    const etaA = parseDate(a._raw?.estimated_delivery_date);
    const etaB = parseDate(b._raw?.estimated_delivery_date);

    switch (filters.dateSort) {
      case "oldest":
        return bookingA - bookingB;
      case "eta_soonest":
        return (etaA || Number.MAX_SAFE_INTEGER) - (etaB || Number.MAX_SAFE_INTEGER);
      case "eta_latest":
        return (etaB || 0) - (etaA || 0);
      case "newest":
      default:
        return bookingB - bookingA;
    }
  });

  return result;
}

export function formatStatusFilterLabel(status: string) {
  if (status === "all") return "All statuses";
  return getShipmentStatusLabel(status);
}

export const DATE_SORT_OPTIONS: { value: ShipmentDateSort; label: string }[] = [
  { value: "newest", label: "Newest first" },
  { value: "oldest", label: "Oldest first" },
  { value: "eta_soonest", label: "ETA soonest" },
  { value: "eta_latest", label: "ETA latest" },
];
